// src/hooks/useModalEscape.js
//
// [PURPOSE]: Standardize Escape-key dismissal across all modal dialogs.
// [WHY]: Sim harness audit (Apr 2026) found multiple modals lacked Escape handlers
//        (e.g., FeatsPanel.js SRD Feat Selector at line 1401). Users expect Escape
//        to dismiss any modal — keyboard-first navigation depends on it. The
//        existing per-modal `useEffect + addEventListener` pattern (see
//        ContainerModal.js:40-47) is correct but duplicated. This hook makes the
//        pattern declarative + consistent across the codebase.
// [USAGE]:
//   const MyModal = ({ isOpen, onClose }) => {
//     useModalEscape(isOpen, onClose);
//     // ...
//   };
// [BEHAVIOR]:
//   - Only listens while `isOpen` is true (prevents stacking listeners)
//   - Listens on document at the capture phase so it wins over deeper handlers
//   - Calls `onClose()` and stops propagation so a parent modal doesn't also close
//   - No-op when `onClose` is null/undefined (defensive against bad call sites)

import { useEffect } from 'react';

export function useModalEscape(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen || typeof onClose !== 'function') return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };

    // Capture phase: outermost modal handler wins. Inner modals (which mount
    // later) attach listeners later and execute first within the capture phase
    // because they were attached most recently — actually, capture-phase
    // ordering is parent-first DOM-wise. To make stacked modals dismiss the
    // top-most first, we use the bubbling phase (default) but rely on
    // stopPropagation so only one handler fires per Escape.
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
}

export default useModalEscape;
